import {
  DEFAULT_SUPPORA_CONFIG,
  createProtocolMessage,
  isWidgetToHostMessage,
  normalizeConfig,
  sanitizeConfig,
  sanitizeIdentity,
  sanitizeMetadata,
  type SupporaEventHandler,
  type SupporaEventName,
  type SupporaEventPayload,
  type SupporaHostCommand,
  type SupporaHostCommandPayload,
  type SupporaIdentity,
  type SupporaMetadata,
  type SupporaPageContext,
  type SupporaPublicConfig,
  type SupporaSafeConfig,
  type SupporaProtocolMessage,
  type SupporaWidgetToHostMessage,
} from '@suppora/shared';

type AnyEventHandler = (payload: SupporaEventPayload) => void;
type SupporaCommandTuple =
  | ['boot', SupporaPublicConfig?]
  | ['open']
  | ['close']
  | ['toggle']
  | ['show']
  | ['hide']
  | ['identify', SupporaIdentity]
  | ['logout']
  | ['setMetadata', SupporaMetadata]
  | ['configure', SupporaPublicConfig]
  | ['on', SupporaEventName, SupporaEventHandler]
  | ['off', SupporaEventName, SupporaEventHandler];

interface SupporaGlobal {
  (...args: SupporaCommandTuple): void;
  q?: SupporaCommandTuple[];
  version?: string;
}

interface SupporaLoaderInstance {
  api: SupporaGlobal;
}

declare global {
  interface Window {
    Suppora?: SupporaGlobal;
    SupporaSettings?: SupporaPublicConfig;
    __supporaLoaderInstance__?: SupporaLoaderInstance;
  }
}

const LOADER_VERSION = '0.1.0';
const DESKTOP_PANEL_WIDTH = 404;
const DESKTOP_PANEL_HEIGHT = 684;
const MOBILE_BREAKPOINT = 520;
const VIEWPORT_MARGIN = 16;
const CLOSED_HEIGHT = 64;
const CLOSED_WIDTH_WITH_LABEL = 184;
const CLOSED_WIDTH_ICON_ONLY = 64;
const MAX_INIT_RETRY_ATTEMPTS = 8;
const MAX_PENDING_COMMANDS = 100;
const PAGE_CONTEXT_POLL_INTERVAL_MS = 1_000;

type LoaderErrorCode =
  | 'invalid_widget_url'
  | 'invalid_api_base_url'
  | 'widget_mount_failed'
  | 'widget_init_timeout'
  | 'command_queue_full';

type ConfigValidationResult =
  { ok: true; config: SupporaSafeConfig } | { ok: false; code: LoaderErrorCode; message: string };

class SupporaLoaderController {
  private config: SupporaSafeConfig = DEFAULT_SUPPORA_CONFIG;
  private pendingBootConfig: SupporaSafeConfig | null = null;
  private iframe: HTMLIFrameElement | null = null;
  private widgetOrigin = '';
  private ready = false;
  private booted = false;
  private waitingForDocumentBody = false;
  private open = false;
  private visible = true;
  private identity: SupporaIdentity | null = null;
  private metadata: SupporaMetadata = {};
  private pageContext: SupporaPageContext = capturePageContext();
  private pageContextPollHandle = 0;
  private titleObserver: MutationObserver | null = null;
  private initRetryHandle = 0;
  private initRetryAttempts = 0;
  private initializationTimedOut = false;
  private pendingQueueOverflowReported = false;
  private readonly pendingCommands: Array<() => void> = [];
  private readonly eventHandlers = new Map<SupporaEventName, Set<AnyEventHandler>>();

  constructor(private readonly defaultWidgetUrl: string) {}

  invoke(args: SupporaCommandTuple): void {
    const [command] = args;
    if (command === 'boot') {
      this.boot(args[1]);
      return;
    }

    if (command === 'on') {
      this.on(args[1], args[2]);
      return;
    }

    if (command === 'off') {
      this.off(args[1], args[2]);
      return;
    }

    if (!this.booted) {
      this.enqueuePendingCommand(() => this.invoke(args));
      return;
    }

    switch (command) {
      case 'open':
      case 'close':
      case 'toggle':
      case 'show':
      case 'hide':
        this.handleVisibilityCommand(command);
        break;
      case 'identify':
        this.identity = sanitizeIdentity(args[1]);
        this.postCommand('identify', { identity: this.identity });
        break;
      case 'logout':
        this.identity = null;
        this.metadata = {};
        this.postCommand('logout');
        break;
      case 'setMetadata':
        this.metadata = {
          ...this.metadata,
          ...sanitizeMetadata(args[1]),
        };
        this.postCommand('setMetadata', { metadata: this.metadata });
        break;
      case 'configure':
        this.configure(args[1]);
        break;
    }
  }

  hasBootRequest(): boolean {
    return this.booted || this.pendingBootConfig !== null;
  }

  private boot(configInput?: SupporaPublicConfig): void {
    if (this.booted) {
      this.configure(configInput ?? {});
      return;
    }

    const fallback =
      this.pendingBootConfig ??
      ({
        ...DEFAULT_SUPPORA_CONFIG,
        widgetUrl: this.defaultWidgetUrl,
      } satisfies SupporaSafeConfig);
    const input = this.pendingBootConfig
      ? sanitizeConfig(configInput)
      : {
          ...sanitizeConfig(window.SupporaSettings),
          ...sanitizeConfig(configInput),
        };
    const result = validateAndResolveConfig({
      ...fallback,
      ...input,
    });
    if (!result.ok) {
      this.emitLoaderError(result.code, result.message);
      return;
    }

    this.pendingBootConfig = result.config;
    this.mountWhenDocumentBodyIsReady();
  }

  private configure(configInput: SupporaPublicConfig): void {
    const result = validateAndResolveConfig(normalizeConfig(configInput, this.config));
    if (!result.ok) {
      this.emitLoaderError(result.code, result.message);
      return;
    }

    const previousWidgetUrl = this.config.widgetUrl;
    this.config = result.config;
    this.applyFrameAttributes();
    this.resizeFrame();

    if (previousWidgetUrl !== this.config.widgetUrl && this.iframe) {
      this.ready = false;
      this.initializationTimedOut = false;
      this.initRetryAttempts = 0;
      this.clearInitRetry();
      this.widgetOrigin = new URL(this.config.widgetUrl).origin;
      this.iframe.src = this.config.widgetUrl;
      return;
    }

    if (!this.ready) {
      this.initializationTimedOut = false;
      this.initRetryAttempts = 0;
      this.sendInit();
      this.scheduleInitRetry();
      return;
    }

    this.post(
      createProtocolMessage('host:configure', {
        config: this.config,
      }),
    );
  }

  private on<TEvent extends SupporaEventName>(
    eventName: TEvent,
    handler: SupporaEventHandler<TEvent>,
  ): void {
    const handlers = this.eventHandlers.get(eventName) ?? new Set<AnyEventHandler>();
    handlers.add(handler as AnyEventHandler);
    this.eventHandlers.set(eventName, handlers);
  }

  private off<TEvent extends SupporaEventName>(
    eventName: TEvent,
    handler: SupporaEventHandler<TEvent>,
  ): void {
    const handlers = this.eventHandlers.get(eventName);
    handlers?.delete(handler as AnyEventHandler);
  }

  private emit<TEvent extends SupporaEventName>(
    eventName: TEvent,
    payload: Parameters<SupporaEventHandler<TEvent>>[0],
  ): void {
    const handlers = this.eventHandlers.get(eventName);
    if (!handlers) {
      return;
    }

    for (const handler of handlers) {
      try {
        handler(payload as SupporaEventPayload);
      } catch (error) {
        this.log('Suppora event handler failed', error);
      }
    }
  }

  private createFrame(config: SupporaSafeConfig): HTMLIFrameElement {
    const iframe = document.createElement('iframe');
    iframe.title = `${config.companyName || 'Suppora'} support`;
    iframe.src = config.widgetUrl;
    iframe.loading = 'lazy';
    iframe.referrerPolicy = 'strict-origin-when-cross-origin';
    iframe.setAttribute('data-suppora-widget', 'true');
    iframe.setAttribute('allowtransparency', 'true');
    iframe.setAttribute('aria-label', `${config.companyName || 'Suppora'} support widget`);
    iframe.addEventListener('load', this.handleFrameLoad);
    this.styleFrame(iframe, config, config.initialOpen, true);
    return iframe;
  }

  private mountWhenDocumentBodyIsReady(): void {
    if (document.body) {
      this.mountPendingFrame();
      return;
    }

    if (this.waitingForDocumentBody) {
      return;
    }

    this.waitingForDocumentBody = true;
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', this.handleDocumentBodyReady, {
        once: true,
      });
      return;
    }

    window.setTimeout(this.handleDocumentBodyReady, 0);
  }

  private readonly handleDocumentBodyReady = (): void => {
    this.waitingForDocumentBody = false;
    if (!document.body) {
      this.pendingBootConfig = null;
      this.emitLoaderError(
        'widget_mount_failed',
        'Suppora could not find document.body. Boot can be retried after the page body is available.',
      );
      return;
    }

    this.mountPendingFrame();
  };

  private mountPendingFrame(): void {
    const config = this.pendingBootConfig;
    const body = document.body;
    if (!config || !body || this.booted) {
      return;
    }

    let iframe: HTMLIFrameElement;
    try {
      iframe = this.createFrame(config);
      body.appendChild(iframe);
    } catch (error) {
      this.pendingBootConfig = null;
      this.emitLoaderError(
        'widget_mount_failed',
        'Suppora could not mount its iframe. Boot can be retried after the page is ready.',
      );
      this.log('Suppora iframe mount failed', error);
      return;
    }

    this.config = config;
    this.pendingBootConfig = null;
    this.iframe = iframe;
    this.widgetOrigin = new URL(config.widgetUrl).origin;
    this.open = config.initialOpen;
    this.booted = true;
    this.pageContext = capturePageContext();
    this.bindHostListeners();
    this.resizeFrame();
  }

  private bindHostListeners(): void {
    window.addEventListener('message', this.handleMessage);
    window.addEventListener('resize', this.handleResize, { passive: true });
    window.visualViewport?.addEventListener('resize', this.handleResize, { passive: true });
    window.addEventListener('hashchange', this.handlePageContextChange);
    window.addEventListener('popstate', this.handlePageContextChange);
    window.addEventListener('pageshow', this.handlePageContextChange);
    document.addEventListener('visibilitychange', this.handlePageContextChange);
    this.wrapHistoryMethod('pushState');
    this.wrapHistoryMethod('replaceState');
    if (document.head && typeof MutationObserver !== 'undefined') {
      this.titleObserver = new MutationObserver(this.handlePageContextChange);
      this.titleObserver.observe(document.head, {
        childList: true,
        characterData: true,
        subtree: true,
      });
    }
    if (this.pageContextPollHandle === 0) {
      this.pageContextPollHandle = window.setInterval(
        this.handlePageContextChange,
        PAGE_CONTEXT_POLL_INTERVAL_MS,
      );
    }
  }

  private readonly handleFrameLoad = (): void => {
    this.ready = false;
    this.initializationTimedOut = false;
    this.initRetryAttempts = 0;
    this.sendInit();
    this.scheduleInitRetry();
  };

  private readonly handleResize = (): void => {
    this.resizeFrame();
  };

  private readonly handlePageContextChange = (): void => {
    const page = capturePageContext();
    if (isSamePageContext(page, this.pageContext)) {
      return;
    }

    this.pageContext = page;
    if (!this.ready) {
      return;
    }

    this.post(
      createProtocolMessage('host:context', {
        page,
      }),
    );
  };

  private wrapHistoryMethod(method: 'pushState' | 'replaceState'): void {
    const original = window.history[method];
    const handlePageContextChange = this.handlePageContextChange;
    const wrapped = function (this: History, ...args: Parameters<History['pushState']>): void {
      Reflect.apply(original, this, args);
      handlePageContextChange();
    };

    try {
      window.history[method] = wrapped;
    } catch (error) {
      // Some hosts freeze History. Polling remains as a non-invasive fallback.
      this.log(`Could not observe history.${method}`, error);
    }
  }

  private readonly handleMessage = (event: MessageEvent<unknown>): void => {
    if (!this.iframe || event.source !== this.iframe.contentWindow) {
      return;
    }

    if (event.origin !== this.widgetOrigin) {
      this.log('Ignored widget message from unexpected origin', event.origin);
      return;
    }

    if (!isWidgetToHostMessage(event.data)) {
      return;
    }

    this.receive(event.data);
  };

  private receive(message: SupporaWidgetToHostMessage): void {
    switch (message.type) {
      case 'widget:ready':
        if (this.ready) {
          return;
        }
        this.ready = true;
        this.initializationTimedOut = false;
        this.clearInitRetry();
        this.pageContext = capturePageContext();
        this.post(
          createProtocolMessage('host:context', {
            page: this.pageContext,
          }),
        );
        this.emit('ready', {
          channelId: this.config.channelId,
          config: { ...this.config },
        });
        this.flushPendingCommands();
        break;
      case 'widget:state':
        this.open = message.payload.open;
        this.visible = message.payload.visible;
        this.resizeFrame();
        break;
      case 'widget:event':
        this.emit(message.payload.event, message.payload.payload);
        break;
    }
  }

  private handleVisibilityCommand(
    command: Extract<SupporaHostCommand, 'open' | 'close' | 'toggle' | 'show' | 'hide'>,
  ): void {
    if (command === 'toggle') {
      this.open = !this.open;
    } else if (command === 'open') {
      this.open = true;
    } else if (command === 'close') {
      this.open = false;
    } else if (command === 'show') {
      this.visible = true;
    } else if (command === 'hide') {
      this.visible = false;
    }

    this.resizeFrame();
    // Init already includes the updated state. Replaying a relative toggle after
    // the ready handshake would invert that state a second time.
    this.postCommand(command === 'toggle' ? (this.open ? 'open' : 'close') : command);
  }

  private postCommand(command: SupporaHostCommand, payload?: SupporaHostCommandPayload): void {
    this.post(
      createProtocolMessage(
        'host:command',
        payload === undefined
          ? {
              command,
            }
          : {
              command,
              payload,
            },
      ),
    );
  }

  private post(message: SupporaProtocolMessage): void {
    const send = (): void => {
      this.iframe?.contentWindow?.postMessage(message, this.widgetOrigin);
    };

    if (!this.ready && message.type !== 'host:init') {
      this.enqueuePendingCommand(send);
      return;
    }

    send();
  }

  private sendInit(): void {
    if (!this.iframe) {
      return;
    }

    this.pageContext = capturePageContext();
    this.post(
      createProtocolMessage('host:init', {
        config: this.config,
        state: {
          open: this.open,
          visible: this.visible,
          identity: this.identity,
          metadata: this.metadata,
          page: this.pageContext,
        },
      }),
    );
  }

  private scheduleInitRetry(): void {
    this.clearInitRetry();
    if (this.ready) {
      return;
    }

    if (this.initRetryAttempts >= MAX_INIT_RETRY_ATTEMPTS) {
      if (!this.initializationTimedOut) {
        this.initializationTimedOut = true;
        this.emitLoaderError(
          'widget_init_timeout',
          'Suppora did not receive a ready response from the widget. Initialization can be retried with configure or boot.',
        );
      }
      return;
    }

    const delay = Math.min(800, 50 * 2 ** this.initRetryAttempts);
    this.initRetryHandle = window.setTimeout(() => {
      this.initRetryAttempts += 1;
      this.sendInit();
      this.scheduleInitRetry();
    }, delay);
  }

  private clearInitRetry(): void {
    if (this.initRetryHandle === 0) {
      return;
    }

    window.clearTimeout(this.initRetryHandle);
    this.initRetryHandle = 0;
  }

  private flushPendingCommands(): void {
    if (!this.ready) {
      return;
    }

    const queued = this.pendingCommands.splice(0);
    this.pendingQueueOverflowReported = false;
    for (const command of queued) {
      command();
    }
  }

  private enqueuePendingCommand(command: () => void): void {
    if (this.pendingCommands.length >= MAX_PENDING_COMMANDS) {
      if (!this.pendingQueueOverflowReported) {
        this.pendingQueueOverflowReported = true;
        this.emitLoaderError(
          'command_queue_full',
          `Suppora has not initialized and its ${MAX_PENDING_COMMANDS}-command queue is full. Retry the command after the ready event.`,
        );
      }
      return;
    }

    this.pendingCommands.push(command);
  }

  private emitLoaderError(code: LoaderErrorCode, message: string): void {
    this.emit('error', {
      code,
      message,
      recoverable: true,
    });
    this.log(message);
  }

  private applyFrameAttributes(): void {
    if (!this.iframe) {
      return;
    }

    this.iframe.title = `${this.config.companyName || 'Suppora'} support`;
    this.iframe.setAttribute(
      'aria-label',
      `${this.config.companyName || 'Suppora'} support widget`,
    );
  }

  private applyFrameChrome(): void {
    if (!this.iframe) {
      return;
    }

    this.styleFrame(this.iframe, this.config, this.open, this.visible);
  }

  private styleFrame(
    iframe: HTMLIFrameElement,
    config: SupporaSafeConfig,
    open: boolean,
    visible: boolean,
  ): void {
    const style = iframe.style;
    style.position = 'fixed';
    style.border = '0';
    style.colorScheme =
      config.mode === 'dark' ? 'dark' : config.mode === 'light' ? 'light' : 'normal';
    style.background = 'transparent';
    style.overflow = 'hidden';
    style.display = visible ? 'block' : 'none';
    style.borderRadius = open ? `${config.borderRadius}px` : '999px';
    style.zIndex = String(config.zIndex);
    style.maxWidth = `calc(100vw - ${VIEWPORT_MARGIN * 2}px)`;
    style.maxHeight = `calc(100dvh - ${VIEWPORT_MARGIN * 2}px)`;
    style.opacity = '1';
    style.transition =
      'width 180ms cubic-bezier(0.2, 0, 0, 1), height 180ms cubic-bezier(0.2, 0, 0, 1), opacity 120ms ease';

    if (config.launcherPosition === 'bottom-left') {
      style.left = `${VIEWPORT_MARGIN}px`;
      style.right = 'auto';
    } else {
      style.right = `${VIEWPORT_MARGIN}px`;
      style.left = 'auto';
    }
    style.bottom = `${VIEWPORT_MARGIN}px`;
  }

  private resizeFrame(): void {
    if (!this.iframe) {
      return;
    }

    this.applyFrameChrome();
    if (!this.visible) {
      return;
    }

    const viewportWidth = window.visualViewport?.width ?? window.innerWidth;
    const viewportHeight = window.visualViewport?.height ?? window.innerHeight;
    const availableWidth = Math.max(1, Math.floor(viewportWidth - VIEWPORT_MARGIN * 2));
    const availableHeight = Math.max(1, Math.floor(viewportHeight - VIEWPORT_MARGIN * 2));

    if (this.open) {
      const mobile = viewportWidth <= MOBILE_BREAKPOINT;
      const width = mobile ? availableWidth : Math.min(DESKTOP_PANEL_WIDTH, availableWidth);
      const height = mobile ? availableHeight : Math.min(DESKTOP_PANEL_HEIGHT, availableHeight);
      this.applyWidgetSize(width, height);
      return;
    }

    const closedWidth =
      this.config.showLauncherLabel && this.config.launcherLabel.length > 0
        ? CLOSED_WIDTH_WITH_LABEL
        : CLOSED_WIDTH_ICON_ONLY;
    this.applyWidgetSize(
      Math.min(closedWidth, availableWidth),
      Math.min(CLOSED_HEIGHT, availableHeight),
    );
  }

  private applyWidgetSize(width: number, height: number): void {
    if (!this.iframe) {
      return;
    }

    const safeWidth = Math.max(1, Math.round(width));
    const safeHeight = Math.max(1, Math.round(height));

    this.iframe.style.width = `${safeWidth}px`;
    this.iframe.style.height = `${safeHeight}px`;
  }

  private log(...args: unknown[]): void {
    if (this.config.debug) {
      console.info('[Suppora]', ...args);
    }
  }
}

function resolveDefaultWidgetUrl(): string {
  try {
    const scriptUrl = getCurrentScriptUrl();
    if (!scriptUrl) {
      return new URL('/widget/index.html', window.location.href).toString();
    }

    return new URL('../widget/index.html', scriptUrl).toString();
  } catch {
    return '';
  }
}

function getCurrentScriptUrl(): string {
  const current = document.currentScript;
  if (current instanceof HTMLScriptElement && current.src) {
    return current.src;
  }

  const scripts = Array.from(document.querySelectorAll<HTMLScriptElement>('script[src]'));
  return scripts[scripts.length - 1]?.src ?? '';
}

function validateAndResolveConfig(config: SupporaSafeConfig): ConfigValidationResult {
  const widgetUrl = resolveHttpUrl(config.widgetUrl, false);
  if (widgetUrl === null) {
    return {
      ok: false,
      code: 'invalid_widget_url',
      message:
        'Suppora widgetUrl must resolve to an http(s) URL. Correct it and call boot or configure again.',
    };
  }

  const apiBaseUrl = resolveHttpUrl(config.apiBaseUrl, true);
  if (apiBaseUrl === null) {
    return {
      ok: false,
      code: 'invalid_api_base_url',
      message:
        'Suppora apiBaseUrl must be empty or resolve to an http(s) URL. Correct it and call boot or configure again.',
    };
  }

  return {
    ok: true,
    config: {
      ...config,
      widgetUrl,
      apiBaseUrl,
    },
  };
}

function resolveHttpUrl(value: string, allowEmpty: boolean): string | null {
  const trimmed = value.trim();
  if (trimmed.length === 0) {
    return allowEmpty ? '' : null;
  }

  try {
    const parsed = new URL(trimmed, window.location.href);
    if (parsed.protocol !== 'http:' && parsed.protocol !== 'https:') {
      return null;
    }
    return parsed.toString();
  } catch {
    return null;
  }
}

function capturePageContext(): SupporaPageContext {
  return {
    url: window.location.href,
    title: document.title,
    referrer: document.referrer,
  };
}

function isSamePageContext(left: SupporaPageContext, right: SupporaPageContext): boolean {
  return left.url === right.url && left.title === right.title && left.referrer === right.referrer;
}

if (!window.__supporaLoaderInstance__) {
  const existingSuppora = window.Suppora;
  const queuedCommands = existingSuppora?.q ?? [];
  const controller = new SupporaLoaderController(resolveDefaultWidgetUrl());

  const suppora: SupporaGlobal = (...args: SupporaCommandTuple): void => {
    controller.invoke(args);
  };
  suppora.q = [];
  suppora.version = LOADER_VERSION;
  window.Suppora = suppora;
  window.__supporaLoaderInstance__ = { api: suppora };

  for (const queuedCommand of queuedCommands) {
    controller.invoke(queuedCommand);
  }

  if (!controller.hasBootRequest() && window.SupporaSettings) {
    controller.invoke(['boot']);
  }
}
